import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { authFetch } from '../api';
import { enablePlaybackAudioSession } from '../game/audio/iosAudioSession';
import './calibration.css';

const METRONOME_BPM = 120;
const METRONOME_BEAT_INTERVAL_MS = (60 / METRONOME_BPM) * 1000;
const TAPS_NEEDED = 8;
const TAPS_TO_DISCARD = 2;

interface MetronomeState {
  ctx: AudioContext;
  startTime: number;
  scheduledThrough: number;
  schedulerHandle: number | null;
}

export function CalibrationPage() {
  const { user, refreshMe } = useAuth();
  const [running, setRunning] = useState(false);
  const [taps, setTaps] = useState<number[]>([]);
  const [measuredOffsetMs, setMeasuredOffsetMs] = useState<number | null>(null);
  const [stdDevMs, setStdDevMs] = useState<number | null>(null);
  const [savedOffset, setSavedOffset] = useState<number | null>(user?.audioOffsetMs ?? null);
  const [manualOffset, setManualOffsetRaw] = useState<number>(user?.audioOffsetMs ?? 0);
  // Mirror the server's [-500, 500] clamp on the client so the displayed
  // value never gets out of sync with what would be persisted.
  const setManualOffset = useCallback((ms: number) => {
    if (!Number.isFinite(ms)) return;
    setManualOffsetRaw(Math.max(-500, Math.min(500, Math.round(ms))));
  }, []);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [audioError, setAudioError] = useState<string | null>(null);
  const metronomeRef = useRef<MetronomeState | null>(null);

  useEffect(() => {
    if (user) {
      setSavedOffset(user.audioOffsetMs);
      setManualOffsetRaw(user.audioOffsetMs);
    }
  }, [user]);

  const startMetronome = useCallback(async () => {
    setAudioError(null);
    const Ctx =
      window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) {
      setAudioError('Web Audio is not supported in this browser - calibration unavailable.');
      return;
    }
    const ctx = new Ctx({ latencyHint: 'interactive' });
    // Audible through the iOS silent switch (otherwise the metronome is muted).
    enablePlaybackAudioSession();
    await ctx.resume();
    const startTime = ctx.currentTime + 0.2;

    metronomeRef.current = {
      ctx,
      startTime,
      scheduledThrough: 0,
      schedulerHandle: null,
    };

    const tick = () => {
      const m = metronomeRef.current;
      if (!m) return;
      const lookaheadSec = 0.5;
      while ((m.scheduledThrough * METRONOME_BEAT_INTERVAL_MS) / 1000 < ctx.currentTime - m.startTime + lookaheadSec) {
        scheduleClick(m.ctx, m.startTime + (m.scheduledThrough * METRONOME_BEAT_INTERVAL_MS) / 1000, m.scheduledThrough % 4 === 0);
        m.scheduledThrough += 1;
      }
      m.schedulerHandle = window.setTimeout(tick, 200);
    };
    tick();
    setRunning(true);
    setTaps([]);
    setMeasuredOffsetMs(null);
    setStdDevMs(null);
  }, []);

  const stopMetronome = useCallback(async () => {
    const m = metronomeRef.current;
    if (m) {
      if (m.schedulerHandle !== null) clearTimeout(m.schedulerHandle);
      try {
        await m.ctx.close();
      } catch (err) {
        console.warn('Failed to close metronome ctx:', err);
      }
      metronomeRef.current = null;
    }
    setRunning(false);
  }, []);

  useEffect(() => {
    return () => {
      void stopMetronome();
    };
  }, [stopMetronome]);

  const handleTap = useCallback(() => {
    const m = metronomeRef.current;
    if (!m) return;
    const tapAtSec = m.ctx.currentTime - m.startTime;
    const expectedBeat = Math.round(tapAtSec * (METRONOME_BPM / 60));
    const expectedSec = (expectedBeat * 60) / METRONOME_BPM;
    const offsetMs = (tapAtSec - expectedSec) * 1000;
    setTaps((prev) => [...prev, offsetMs]);
  }, []);

  useEffect(() => {
    if (!running) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        handleTap();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, handleTap]);

  useEffect(() => {
    if (taps.length >= TAPS_NEEDED + TAPS_TO_DISCARD) {
      const usable = [...taps].slice(TAPS_TO_DISCARD).sort((a, b) => a - b);
      const trim = Math.floor(usable.length * 0.1);
      const trimmed = usable.slice(trim, usable.length - trim);
      const mean = trimmed.reduce((a, b) => a + b, 0) / trimmed.length;
      const variance =
        trimmed.reduce((acc, t) => acc + (t - mean) * (t - mean), 0) / trimmed.length;
      setMeasuredOffsetMs(Math.round(mean));
      setStdDevMs(Math.round(Math.sqrt(variance)));
      void stopMetronome();
    }
  }, [taps, stopMetronome]);

  const saveOffset = useCallback(
    async (ms: number) => {
      setSaveStatus('saving');
      try {
        const res = await authFetch('/api/me', {
          method: 'PATCH',
          json: { audioOffsetMs: ms },
        });
        if (!res.ok) throw new Error(`PATCH /api/me failed: ${res.status}`);
        const json = (await res.json()) as { audioOffsetMs?: number };
        const persisted = typeof json.audioOffsetMs === 'number' ? json.audioOffsetMs : ms;
        setSavedOffset(persisted);
        setManualOffsetRaw(persisted);
        setSaveStatus('saved');
        await refreshMe();
        setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 1500);
      } catch (err) {
        console.error('Failed to save offset', err);
        setSaveStatus('error');
      }
    },
    [refreshMe],
  );

  return (
    <div className="calibration-page" data-testid="calibration-page">
      <header>
        <h2>Audio calibration</h2>
        <p className="muted">
          Tap <kbd>Space</kbd> in time with the metronome. After {TAPS_NEEDED} clean taps we will
          measure your average offset and let you save it.
        </p>
        {audioError && (
          <div className="error-box" data-testid="audio-error">
            {audioError}
          </div>
        )}
      </header>

      <div className="calibration-grid">
        <div className="card calibration-tap" data-testid="calibration-card">
          <h3>{running ? 'Tap on every click' : 'Ready when you are'}</h3>
          {!running && measuredOffsetMs === null && (
            <button type="button" className="btn btn-primary btn-large" onClick={() => void startMetronome()} data-testid="start-calibration">
              Start metronome
            </button>
          )}
          {running && (
            <>
              <div
                className="tap-zone"
                onClick={handleTap}
                role="button"
                tabIndex={0}
                aria-label="Tap zone - press Space, Enter or click in time with the metronome"
                onKeyDown={(e) => {
                  if (e.code === 'Space' || e.code === 'Enter') {
                    e.preventDefault();
                    handleTap();
                  }
                }}
              >
                <div className="tap-progress">
                  {taps.length < TAPS_TO_DISCARD ? (
                    <span>warmup {taps.length} / {TAPS_TO_DISCARD}</span>
                  ) : (
                    <span>{taps.length - TAPS_TO_DISCARD} / {TAPS_NEEDED}</span>
                  )}
                </div>
                <p className="muted">
                  {taps.length < TAPS_TO_DISCARD
                    ? 'Find the rhythm. These first taps are warmup.'
                    : 'Tap here or press SPACE on every click.'}
                </p>
              </div>
              <button type="button" className="btn btn-ghost" onClick={() => void stopMetronome()}>
                Cancel
              </button>
            </>
          )}
          {measuredOffsetMs !== null && (
            <div className="calibration-result" data-testid="calibration-result">
              <p>Measured offset:</p>
              <div className="big-number">{measuredOffsetMs > 0 ? '+' : ''}{measuredOffsetMs} ms</div>
              {stdDevMs !== null && (
                <p className="muted">σ = {stdDevMs} ms ({stdDevMs < 30 ? 'tight' : stdDevMs < 60 ? 'okay' : 'inconsistent — try again'})</p>
              )}
              <div className="result-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saveStatus === 'saving'}
                  onClick={() => void saveOffset(measuredOffsetMs)}
                  data-testid="save-measured-offset"
                >
                  {saveStatus === 'saving' ? 'Saving...' : 'Save offset'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setMeasuredOffsetMs(null);
                    setStdDevMs(null);
                    setTaps([]);
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Manual override</h3>
          <p className="muted">If you know your offset, set it directly here. Range -500 to +500 ms.</p>
          <div className="manual-offset">
            <input
              type="range"
              min={-500}
              max={500}
              step={1}
              value={manualOffset}
              aria-label="Manual audio offset (milliseconds)"
              aria-valuetext={`${manualOffset > 0 ? '+' : ''}${manualOffset} milliseconds`}
              onChange={(e) => setManualOffset(Number(e.target.value))}
              data-testid="manual-offset-slider"
            />
            <div className="manual-offset-readout">
              <input
                type="number"
                min={-500}
                max={500}
                value={manualOffset}
                onChange={(e) => setManualOffset(Number(e.target.value))}
              />
              <span>ms</span>
            </div>
          </div>
          <button
            type="button"
            className="btn"
            disabled={saveStatus === 'saving'}
            onClick={() => void saveOffset(manualOffset)}
            data-testid="save-manual-offset"
          >
            {saveStatus === 'saving' ? 'Saving...' : 'Save manual offset'}
          </button>
          {savedOffset !== null && (
            <p className="muted faint">
              Currently saved: <strong data-testid="saved-offset-value">{savedOffset}</strong> ms
              {saveStatus === 'saved' && <span className="ok-pill"> · saved</span>}
              {saveStatus === 'error' && <span className="err-pill" data-testid="save-error-pill"> · error</span>}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <h3>How does this work?</h3>
        <p className="muted">
          Your speakers, browser, and headphones all introduce a small delay before sound reaches
          your ears. We measure how late your taps land relative to the metronome ticks, average
          across taps, and apply the result as a global audio offset. A positive offset means your
          taps were late on average; we then schedule sound earlier in-game so they line up.
        </p>
      </div>
    </div>
  );
}

function scheduleClick(ctx: AudioContext, t0: number, accent: boolean): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'square';
  osc.frequency.value = accent ? 1600 : 800;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(accent ? 0.5 : 0.3, t0 + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + 0.06);
}
